/* eslint-disable import/no-unresolved */
import { useLocation } from '@reach/router';
import PropTypes from 'prop-types';
import React, {
  useContext, useEffect, useState,
} from 'react';

import {
  Components, ScreenType, BlockTypes, GlobalLayoutValues,
} from '@powdr/constants';
import { AppContext } from '@powdr/context/app-context';
import { useSeason, useWindowResize } from '@powdr/hooks';
import { colorProfileByIndex, componentFactory, getBackgroundImageSrc } from '@powdr/utils';

import {
  ComponentWrapper, InnerContainer, StyledUiSection, MaxWidthWrapper,
  OuterWrapper,
} from './styles';

export const UiSection = ({
  className = null,
  colorProfile = null,
  componentSeason = null,
  isContainer = false,
  hideOnPlatform = null,
  isHideSection = false,
  scrollToName = '',
  isTrapezoidOverlay = false,
  removePadding = false,
  topPadding = null,
  bottomPadding = null,
  topPaddingMobile = null,
  bottomPaddingMobile = null,
  sidePaddingPercentage = null,
  isIgnoreMaxWidth = false,
  isTransparent = false,
  parentColorProfile = null,
  isWrapperless = false,
  backgroundCrop = null,
  backgroundCropMobile = null,
  relationships,
}) => {
  const { isMobile, fullHeaderHeight } = useContext(AppContext);
  const { checkSeasonality } = useSeason();
  const resize = useWindowResize();
  const childComponents = relationships?.children;
  const getScrollToName = () => ((scrollToName) ? scrollToName.replace(/[^a-zA-Z0-9-]/ig, '').replace(/ /g, '-').toLowerCase() : '');
  const noPaddingGridBlockTypes = [BlockTypes.COVER];
  const {
    gatsbyImage: backgroundImage,
  } = relationships?.primaryImage?.relationships?.image || {};
  const {
    gatsbyImage: backgroundImageMobile,
  } = relationships?.mobileImage?.relationships?.image || {};
  const [bgImage, setBgImage] = useState('');
  const scrollToHashValue = useLocation()?.hash?.replace('#', '');

  const trueColorProfile = (colorProfile !== null)
    ? colorProfileByIndex(colorProfile)
    : colorProfileByIndex(parentColorProfile);

  useEffect(() => {
    if (resize.windowWidth) {
      setBgImage(
        getBackgroundImageSrc(
          (isMobile) ? backgroundImageMobile : backgroundImage,
          resize.windowWidth,
        ),
      );
    }
  }, [backgroundImage, backgroundImageMobile, isMobile, resize.windowWidth]);

  // handle scroll to for section based on URL hash
  useEffect(() => {
    if (scrollToHashValue) {
      const anchorSection = document.getElementById(scrollToHashValue);
      if (anchorSection) {
        window.scrollTo({
          behavior: 'smooth',
          top: anchorSection.offsetTop - fullHeaderHeight - 50,
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToHashValue]);

  // TODO: Should this be a useCallback so it only fires when components change?
  const hasComponentAsChild = (component) => childComponents?.map(
    (child) => (child.typeName === component),
  ).includes(true);

  // TODO: Should this be a useCallback so it only fires when components change?
  const hasNoPaddingGrid = () => childComponents?.map(
    (child) => (
      !!((child.typeName === Components.GRID
      && noPaddingGridBlockTypes.includes(child?.blockType)))
    ),
  ).includes(true);

  // TODO: Should this be a useCallback so it only fires when isMobile changes?
  // returns true if section should be hidden
  const handleHideSection = () => {
    if (hideOnPlatform) {
      if ((isMobile && hideOnPlatform === ScreenType.MOBILE)
        || (!isMobile && hideOnPlatform === ScreenType.DESKTOP)
      ) return true;
    }
    if (!checkSeasonality(componentSeason)) return true;

    return isHideSection;
  };

  // we get this here instead of in the styled component so that we can pass it on
  // to child components for some cases
  const getSidePadding = () => {
    if (sidePaddingPercentage === null || sidePaddingPercentage === undefined) {
      // legacy support/fallback for container toggle
      if (isContainer) {
        if (isMobile) return GlobalLayoutValues.MOBILE.UI_SECTION_DEFAULT_SIDE_MARGIN;
        return GlobalLayoutValues.DESKTOP.UI_SECTION_DEFAULT_SIDE_MARGIN;
      }

      return 0;
    }

    if (isMobile) return GlobalLayoutValues.MOBILE.UI_SECTION_DEFAULT_SIDE_MARGIN;

    return sidePaddingPercentage;
  };

  // TODO: handle color profile, classes, and dynamic styles via styled component
  return (
    !handleHideSection() && (
      <StyledUiSection
        id={getScrollToName()}
        className={className}
        $colorProfile={trueColorProfile}
        $isTransparent={isTransparent}
        $backgroundImage={bgImage}
        $backgroundCrop={(isMobile) ? backgroundCropMobile : backgroundCrop}
      >
        <OuterWrapper>
          <InnerContainer
            isContainer={isContainer}
            isMobile={isMobile}
            hasUiHeaderAsChild={hasComponentAsChild(Components.UI_HEADER)}
            hasUiFooterAsChild={hasComponentAsChild(Components.UI_FOOTER)}
            hasNoPaddingGrid={hasNoPaddingGrid()}
            removePadding={removePadding}
            $topPadding={topPadding}
            $bottomPadding={bottomPadding}
            $topPaddingMobile={topPaddingMobile}
            $bottomPaddingMobile={bottomPaddingMobile}
            $sidePadding={getSidePadding()}
            $isWrapperless={isWrapperless}
          >
            <MaxWidthWrapper $isIgnoreMaxWidth={isIgnoreMaxWidth}>
              { isTrapezoidOverlay && <div className="trapezoid-overlay" /> }
              <ComponentWrapper>
                {(componentFactory(
                  childComponents,
                  trueColorProfile,
                  componentSeason,
                  {
                    hasPaddableGrid: hasNoPaddingGrid(),
                    sectionMargin: getSidePadding(),
                    isWrapperless,
                  },
                ))}
              </ComponentWrapper>
            </MaxWidthWrapper>
          </InnerContainer>
        </OuterWrapper>
      </StyledUiSection>
    )
  );
};

UiSection.propTypes = {
  className: PropTypes.string,
  colorProfile: PropTypes.number,
  isContainer: PropTypes.bool,
  hideOnPlatform: PropTypes.string,
  isHideSection: PropTypes.bool,
  scrollToName: PropTypes.string,
  componentSeason: PropTypes.string,
  isTrapezoidOverlay: PropTypes.bool,
  removePadding: PropTypes.bool,
  topPadding: PropTypes.number,
  bottomPadding: PropTypes.number,
  topPaddingMobile: PropTypes.number,
  bottomPaddingMobile: PropTypes.number,
  sidePaddingPercentage: PropTypes.number,
  isIgnoreMaxWidth: PropTypes.bool,
  isTransparent: PropTypes.bool,
  isWrapperless: PropTypes.bool,
  backgroundCrop: PropTypes.number,
  backgroundCropMobile: PropTypes.number,
  parentColorProfile: PropTypes.string,
  relationships: PropTypes.shape({
    id: PropTypes.string,
    children: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
      }),
    ),
    primaryImage: PropTypes.shape({
      relationships: PropTypes.shape({
        image: PropTypes.shape(),
      }),
    }),
    mobileImage: PropTypes.shape({
      relationships: PropTypes.shape({
        image: PropTypes.shape(),
      }),
    }),
  }).isRequired,
};
